import { useState } from 'react';
import { Printer } from 'lucide-react';
import { Modal } from './Modal';
import { MarkdownBody } from './MarkdownBody';
import type { EventDay } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  content: string;
  days?: EventDay[];
  eventTitle?: string;
}

function dayLabel(d: EventDay): string {
  const date = new Date(d.day_date + 'T00:00:00Z');
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  return 'Day ' + d.day_index + ' — ' + dateStr;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Lightweight markdown→HTML converter for the print window. Supports headings,
// bold/italic, GFM tables, ordered/unordered lists, blockquotes, hr, paragraphs,
// inline code, links. Replaces the legacy bullet character with a dash so
// react-markdown‐style content prints cleanly.
function mdToHtml(src: string): string {
  let md = src.replace(/^(\s*)•\s+/gm, '$1- ');
  // Escape HTML first, then restore our markdown syntax
  md = esc(md);

  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Horizontal rule
    if (/^(-{3,}|\*{3,})\s*$/.test(line)) { out.push('<hr/>'); i++; continue; }
    // Heading
    let m = /^(#{1,6})\s+(.+)$/.exec(line);
    if (m) { const lvl = m[1].length; out.push('<h' + lvl + '>' + inline(m[2]) + '</h' + lvl + '>'); i++; continue; }
    // Blockquote
    if (/^&gt;\s+/.test(line)) {
      let chunk = line.replace(/^&gt;\s+/, '');
      while (i + 1 < lines.length && /^&gt;\s+/.test(lines[i + 1])) {
        chunk += ' ' + lines[i + 1].replace(/^&gt;\s+/, '');
        i++;
      }
      out.push('<blockquote>' + inline(chunk) + '</blockquote>');
      i++; continue;
    }
    // Table (markdown pipe table)
    if (/^\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\|[-\s:|]+\|\s*$/.test(lines[i + 1])) {
      const header = line.split('|').slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i].split('|').slice(1, -1).map((c) => c.trim()));
        i++;
      }
      let t = '<table><thead><tr>' + header.map((h) => '<th>' + inline(h) + '</th>').join('') + '</tr></thead><tbody>';
      for (const r of rows) t += '<tr>' + r.map((c) => '<td>' + inline(c) + '</td>').join('') + '</tr>';
      t += '</tbody></table>';
      out.push(t); continue;
    }
    // Unordered list
    if (/^[\-\*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[\-\*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*]\s+/, ''));
        i++;
      }
      out.push('<ul>' + items.map((it) => '<li>' + inline(it) + '</li>').join('') + '</ul>');
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      out.push('<ol>' + items.map((it) => '<li>' + inline(it) + '</li>').join('') + '</ol>');
      continue;
    }
    // Blank line
    if (/^\s*$/.test(line)) { i++; continue; }
    // Paragraph — gather lines until blank
    let para = line;
    while (i + 1 < lines.length && !/^\s*$/.test(lines[i + 1]) && !/^(#{1,6}\s|\|.*\|\s*$|[\-\*]\s|\d+\.\s|&gt;\s|---|\*\*\*)/.test(lines[i + 1])) {
      i++;
      para += ' ' + lines[i];
    }
    out.push('<p>' + inline(para) + '</p>');
    i++;
  }
  return out.join('\n');
}

function inline(s: string): string {
  // bold + italic + inline code + links
  let t = s;
  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/\*(.+?)\*/g, '<em>$1</em>');
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>');
  t = t.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2">$1</a>');
  return t;
}

function openPrint(title: string, days: EventDay[], fallback: string): void {
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) return;
  let pages = '';
  for (const d of days) {
    const md = d.agenda_md || fallback || '';
    const html = mdToHtml(md) || '<p><em>No agenda provided.</em></p>';
    pages += '<div class="page"><h1>' + esc(title) + '</h1><p class="sub">' + esc(dayLabel(d)) + '</p><h2>Agenda</h2><div class="body">' + html + '</div></div>';
  }
  const css = [
    'body{font-family:ui-sans-serif,system-ui,sans-serif;color:#1f2937;margin:0;}',
    '.page{padding:1in;page-break-after:always;}',
    '.page:last-child{page-break-after:auto;}',
    'h1{color:#3a5d44;font-size:24pt;margin:0 0 4pt 0;}',
    'h2{color:#3a5d44;font-size:14pt;border-bottom:1px solid #d4d4d8;padding-bottom:6pt;margin:24pt 0 12pt;}',
    'h3{color:#3a5d44;font-size:12pt;margin-top:18pt;}',
    'h4,h5,h6{color:#3a5d44;margin-top:12pt;}',
    '.sub{color:#6b7280;margin:0 0 12pt 0;}',
    '.body p,.body li{line-height:1.5;}',
    '.body ul,.body ol{padding-left:24pt;}',
    'em{font-style:italic;}',
    'strong{font-weight:600;color:#111827;}',
    'blockquote{margin:8pt 0;padding:6pt 12pt;border-left:3pt solid #3a5d44;background:#f4f4f5;color:#374151;font-style:italic;}',
    'table{width:100%;border-collapse:collapse;margin:12pt 0;font-size:10pt;}',
    'th,td{border:1px solid #d4d4d8;padding:6pt 8pt;text-align:left;vertical-align:top;}',
    'th{background:#f4f4f5;font-weight:600;}',
    'code{background:#f4f4f5;padding:1pt 4pt;border-radius:2pt;font-family:ui-monospace,monospace;font-size:90%;}',
    'a{color:#3a5d44;}',
    'hr{border:none;border-top:1px solid #d4d4d8;margin:18pt 0;}',
  ].join('');
  w.document.write('<html><head><title>' + esc(title) + ' — Agenda</title><style>' + css + '</style></head><body>' + pages + '<script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>');
  w.document.close();
}

export function AgendaModal(props: Props) {
  const { open, onClose, content, days, eventTitle = 'Workshop' } = props;
  const isMulti = !!(days && days.length > 1);
  const [active, setActive] = useState(0);
  const shown = isMulti ? (days![active]?.agenda_md ?? content) : content;
  return (
    <Modal open={open} onClose={onClose} title="Workshop agenda" maxWidth="max-w-3xl">
      <div className="space-y-4">
        {isMulti && (
          <div className="flex items-center justify-between gap-2 flex-wrap border-b border-gray-200 pb-2">
            <div className="flex gap-1 flex-wrap" role="tablist">
              {days!.map((d, i) => (
                <button
                  key={d.day_index}
                  type="button"
                  role="tab"
                  aria-selected={active === i}
                  onClick={() => setActive(i)}
                  className={
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
                    (active === i
                      ? 'bg-uif-green text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50')
                  }
                >
                  {dayLabel(d)}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => openPrint(eventTitle, days!, content)}
              className="inline-flex items-center gap-1.5 rounded-md border border-uif-green/40 px-3 py-1.5 text-sm text-uif-green-dark hover:bg-uif-green/5"
            >
              <Printer className="h-4 w-4" />
              Print all days
            </button>
          </div>
        )}
        <div>
          <MarkdownBody source={shown} />
        </div>
      </div>
    </Modal>
  );
}
